/**
 * CSRF Token Service
 * Handles fetching and managing CSRF tokens for secure API requests
 */

import { env } from './environment';

class CSRFService {
  private csrfToken: string | null = null;
  private tokenExpiry: number | null = null;
  private fetchingPromise: Promise<string> | null = null;

  /**
   * Get a valid CSRF token, fetching a new one if needed
   */
  async getCSRFToken(): Promise<string> {
    // If we're already fetching a token, wait for that promise
    if (this.fetchingPromise) {
      return this.fetchingPromise;
    }

    // Check if current token is still valid (not expired)
    if (this.csrfToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.csrfToken;
    }

    // Fetch a new token
    this.fetchingPromise = this.fetchNewToken();
    
    try {
      const token = await this.fetchingPromise;
      return token;
    } finally {
      this.fetchingPromise = null;
    }
  }

  /**
   * Fetch a new CSRF token from the server
   */
  private async fetchNewToken(): Promise<string> {
    const sessionId = localStorage.getItem("sessionId");
    
    if (!sessionId) {
      throw new Error("No session available for CSRF token");
    }

    try {
      const response = await fetch(`${env.apiUrl}/api/auth/csrf-token`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${sessionId}`,
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.csrf_token) {
        throw new Error("No CSRF token in response");
      }

      // Store the token and set expiry (23 hours to be safe, server has 24 hour expiry)
      this.csrfToken = data.csrf_token;
      this.tokenExpiry = Date.now() + (23 * 60 * 60 * 1000); // 23 hours

      console.log("✅ CSRF token fetched successfully");
      return this.csrfToken!; // Non-null assertion since we just assigned it

    } catch (error) {
      console.error("❌ Failed to fetch CSRF token:", error);
      throw error;
    }
  }

  /**
   * Clear the stored CSRF token (e.g., on logout)
   */
  clearToken(): void {
    this.csrfToken = null;
    this.tokenExpiry = null;
    this.fetchingPromise = null;
  }

  /**
   * Check if a request method requires CSRF protection
   */
  requiresCSRF(method: string): boolean {
    const protectedMethods = ["POST", "PUT", "PATCH", "DELETE"];
    return protectedMethods.includes(method.toUpperCase());
  }
}

// Export a singleton instance
export const csrfService = new CSRFService(); 